/**
 * What each route tells a search engine, and an answer engine, about itself.
 *
 * One source with two readers. At runtime `<Seo />` renders these as React 19
 * <title>/<meta>/<link> tags, which React hoists into <head>, so the tab title
 * stays right across client-side navigation. At build time
 * scripts/prerender.mjs loads every route in a headless browser and saves the
 * DOM after React has mounted -- the hoisted tags are already in it, so there
 * is no second injection path to keep in step with the first.
 *
 * Every fact is read from the module that already owns it: prices from
 * pricing.js, the entity from company.js, the questions from faq.js. The same
 * discipline as gen-shots.mjs -- a price quoted in structured data that the
 * pricing page does not show is exactly the mismatch Google's structured-data
 * policy penalises, and the one a Razorpay reviewer would notice.
 *
 * `__tests__/seo.test.js` holds the rules: every route in App.js has an entry,
 * titles are unique, descriptions fit a results page, canonicals are absolute.
 */

import { COMPANY } from "./company";
import { FAQ } from "./faq";
import { TIERS, TERMS, perDay } from "./pricing";
import { ABOUT, DELETE_ACCOUNT, HELP, PRIVACY, REFUNDS, TERMS as TERMS_PAGE } from "./legal";
import { CONTENT_ROUTES, byPath } from "./registry";

const { product, legalName, supportEmail, urls } = COMPANY;

export const SITE = urls.site;

/** 1200x630, generated by scripts/og-image.mjs. */
export const OG_IMAGE = `${SITE}/og.png`;

export const absolute = (path) => (path === "/" ? `${SITE}/` : `${SITE}${path}`);

/**
 * Title and description per route.
 *
 * Titles lead with what somebody searches for, not with the brand: nobody
 * searching for gym software has heard of Varzish Mate yet. Descriptions stay
 * under ~155 characters, which is where Google truncates on desktop.
 */
const STATIC_PAGES = {
  "/": {
    title: `${product} — Gym Management Software for Indian Gyms`,
    description: `Run your gym from your phone: members, payments, QR attendance, workout plans and reminders in one place. Android, iPhone or browser. Free to start, no card.`,
    h1: "Run your gym",
  },
  "/pricing": {
    title: `Gym Management Software Price in India | ${product}`,
    description: `Plans from ₹${TIERS[0].prices[TERMS[0]].toLocaleString("en-IN")} for ${TERMS[0]} months + GST, about ₹${perDay(TIERS[0].prices[TERMS[0]], TERMS[0])} a day. Every feature on every plan. ${COMPANY.trialDays} days free, no card.`,
    h1: "Pricing",
  },
  "/about": {
    title: `About ${product} — Gym Software by ${legalName}`,
    description: `${product} is gym management software for gym owners, built and operated by ${legalName} in Rajasthan, India.`,
    h1: ABOUT.title,
  },
  "/help": {
    title: `Help and Support | ${product}`,
    description: `Getting started with ${product}: creating your club, adding members, recording payments, reminders, invoices and GST. We answer within one working day.`,
    h1: HELP.title,
  },
  "/contact": {
    title: `Contact ${product} — Support and Grievance Officer`,
    description: `Email ${supportEmail} for account, plan, payment or refund questions. Registered address, GSTIN and the DPDP Act grievance officer for ${legalName}.`,
    h1: "Contact",
  },
  "/privacy": {
    title: `Privacy Policy | ${product}`,
    description: `How ${legalName} collects, uses, stores and deletes the information gym owners and their members give ${product}, and how to exercise your rights.`,
    h1: PRIVACY.title,
  },
  "/terms": {
    title: `Terms of Service | ${product}`,
    description: `The agreement between you and ${legalName} for using ${product}: accounts, subscriptions, acceptable use, your data and how the service can end.`,
    h1: TERMS_PAGE.title,
  },
  "/refunds": {
    title: `Refund and Cancellation Policy | ${product}`,
    description: `What happens when you cancel a ${product} subscription, when a payment is refunded, and how long a refund takes to reach your account.`,
    h1: REFUNDS.title,
  },
  "/delete-account": {
    title: `Delete Your Account | ${product} and Lfit`,
    description: `How to delete a ${product} club or a Lfit member account from the app, what is removed, what is kept, and how to ask by email if you cannot sign in.`,
    h1: DELETE_ACCOUNT.title,
  },
};

/** The nine hand-built pages. Their components stay in App.js. */
export const STATIC_ROUTES = Object.keys(STATIC_PAGES);

/**
 * Static pages plus every content page in the registry. Content titles,
 * descriptions and h1s come from frontmatter via routes.generated.json.
 */
export const PAGES = {
  ...STATIC_PAGES,
  ...Object.fromEntries(
    CONTENT_ROUTES.map(({ path, title, description, h1 }) => [path, { title, description, h1 }])
  ),
};

export const ROUTES = Object.keys(PAGES);

/** The section a content type lives under, for breadcrumbs and hub pages. */
const HUBS = {
  guide: { label: "Guides", path: "/guides" },
  compare: { label: "Compare", path: "/compare" },
  tool: { label: "Free tools", path: "/tools" },
  hindi: { label: "Hindi guides", path: "/hi" },
  research: { label: "Research", path: "/research" },
  vertical: { label: "Gym types", path: "/gyms" },
};

/**
 * {label, path} of the hub for a content type, or null. Features sit at the
 * root, and a section whose hub page does not exist has no crumb: a
 * BreadcrumbList that points at a missing page is a broken link to Google.
 */
export const hubFor = (type) => {
  const hub = HUBS[type];
  return hub && byPath(hub.path) ? hub : null;
};

// ── JSON-LD ────────────────────────────────────────────────────────────────

/**
 * The registered address, split for PostalAddress.
 *
 * company.js holds it as one string because that is how every page prints it.
 * The last three comma-separated parts are always city, state and PIN; the
 * test pins that so an edit that reorders them fails loudly.
 */
export function postalAddress(address = COMPANY.address) {
  const parts = address.split(",").map((s) => s.trim());
  const postalCode = parts.pop();
  const addressRegion = parts.pop();
  const addressLocality = parts.pop();
  return {
    "@type": "PostalAddress",
    streetAddress: parts.join(", "),
    addressLocality,
    addressRegion,
    postalCode,
    addressCountry: "IN",
  };
}

const ORG_ID = `${SITE}/#organization`;
const APP_ID = `${SITE}/#software`;

export const organization = ({ full = false } = {}) => ({
  "@type": "Organization",
  "@id": ORG_ID,
  name: product,
  legalName,
  url: `${SITE}/`,
  logo: `${SITE}/logo512.png`,
  email: supportEmail,
  ...(COMPANY.sameAs.length > 0 && { sameAs: COMPANY.sameAs }),
  ...(full && {
    description: COMPANY.entity,
    address: postalAddress(),
    ...(COMPANY.gstin && { taxID: COMPANY.gstin }),
    contactPoint: contactPoint(),
  }),
});

export const contactPoint = () => ({
  "@type": "ContactPoint",
  contactType: "customer support",
  email: supportEmail,
  areaServed: "IN",
  availableLanguage: ["en"],
});

export const website = () => ({
  "@type": "WebSite",
  "@id": `${SITE}/#website`,
  name: product,
  url: `${SITE}/`,
  publisher: { "@id": ORG_ID },
  inLanguage: "en-IN",
});

/** One Offer per tier and term -- the nine prices the pricing page can show. */
export const offers = () =>
  TIERS.flatMap((tier) =>
    TERMS.map((months) => ({
      "@type": "Offer",
      name: `${tier.name}, ${months} months`,
      price: String(tier.prices[months]),
      priceCurrency: "INR",
      url: absolute("/pricing"),
      availability: "https://schema.org/InStock",
      // Prices are BEFORE GST (added at checkout since 2026-10-09); see pricing.js.
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: String(tier.prices[months]),
        priceCurrency: "INR",
        valueAddedTaxIncluded: false,
        billingDuration: `P${months}M`,
      },
    }))
  );

/**
 * `priced: false` leaves the offer out entirely: the home page shows no price
 * (owner's call, 2026-10-15), and structured data should not claim what the
 * page does not show. /pricing carries every offer; feature pages the range.
 */
export const softwareApplication = ({ withOffers = false, priced = true } = {}) => {
  const all = TIERS.flatMap((t) => Object.values(t.prices));
  return {
    "@type": "SoftwareApplication",
    "@id": APP_ID,
    name: product,
    applicationCategory: "BusinessApplication",
    applicationSubCategory: "Gym management software",
    operatingSystem: "Android, iOS, Web",
    url: `${SITE}/`,
    description: PAGES["/"].description,
    publisher: { "@id": ORG_ID },
    installUrl: [urls.playClub, urls.appStoreClub],
    ...(priced && {
      offers: withOffers
        ? offers()
        : {
            "@type": "AggregateOffer",
            priceCurrency: "INR",
            lowPrice: String(Math.min(...all)),
            highPrice: String(Math.max(...all)),
            offerCount: String(all.length),
          },
    }),
  };
};

export const faqPage = () => ({
  "@type": "FAQPage",
  mainEntity: FAQ.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
});

export const breadcrumbs = (path) => {
  const entry = byPath(path);
  const hub = entry && entry.type !== "static" && entry.type !== "hub" ? hubFor(entry.type) : null;
  const trail = [
    { name: "Home", item: `${SITE}/` },
    ...(hub ? [{ name: hub.label, item: absolute(hub.path) }] : []),
    { name: PAGES[path].h1, item: absolute(path) },
  ];
  return {
    "@type": "BreadcrumbList",
    itemListElement: trail.map((t, i) => ({ "@type": "ListItem", position: i + 1, ...t })),
  };
};

const webPage = (path) => ({
  "@type": path === "/about" ? "AboutPage" : path === "/contact" ? "ContactPage" : "WebPage",
  "@id": `${absolute(path)}#webpage`,
  url: absolute(path),
  name: PAGES[path].title,
  description: PAGES[path].description,
  isPartOf: { "@id": `${SITE}/#website` },
  inLanguage: byPath(path)?.lang ?? "en-IN",
});

/** The byline entity: the same Organization node as everywhere else. */
const TEAM = { "@type": "Organization", "@id": ORG_ID, name: "Varzish Mate team" };

const article = (path, entry) => ({
  "@type": "Article",
  "@id": `${absolute(path)}#article`,
  headline: entry.h1,
  description: entry.description,
  author: TEAM,
  publisher: TEAM,
  datePublished: entry.published,
  dateModified: entry.updated,
  inLanguage: entry.lang,
  mainEntityOfPage: { "@type": "WebPage", "@id": absolute(path) },
});

const dataset = (path, entry) => ({
  "@type": "Dataset",
  "@id": `${absolute(path)}#dataset`,
  name: entry.h1,
  description: entry.description,
  url: absolute(path),
  creator: TEAM,
  datePublished: entry.published,
  dateModified: entry.updated,
  inLanguage: entry.lang,
});

const webApplication = (path, entry) => ({
  "@type": "WebApplication",
  "@id": `${absolute(path)}#tool`,
  name: entry.h1,
  description: entry.description,
  url: absolute(path),
  applicationCategory: "BusinessApplication",
  operatingSystem: "Any",
  isAccessibleForFree: true,
  inLanguage: entry.lang,
  publisher: { "@id": ORG_ID },
});

const itemList = (path, entry) => ({
  "@type": "ItemList",
  "@id": `${absolute(path)}#list`,
  name: entry.h1,
  itemListElement: entry.itemList.map((item, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: item.name,
    url: `${absolute(path)}#${item.id}`,
  })),
});

/** Graph nodes for a content page, by type. Breadcrumbs are added by the caller. */
const contentNodes = (path, entry) => {
  const extra = entry.itemList?.length ? [itemList(path, entry)] : [];
  return [...baseNodes(path, entry), ...extra];
};

const baseNodes = (path, entry) => {
  switch (entry.type) {
    case "guide":
    case "hindi":
      return [article(path, entry)];
    case "research":
      return [article(path, entry), dataset(path, entry)];
    case "feature":
    case "vertical":
      return [webPage(path), softwareApplication()];
    case "tool":
      return [webApplication(path, entry)];
    default:
      return [webPage(path)];
  }
};

/** The JSON-LD graph for a route, as one object. */
export function jsonLd(path) {
  const entry = byPath(path);
  if (entry && entry.type !== "static") {
    return {
      "@context": "https://schema.org",
      "@graph": [...contentNodes(path, entry), breadcrumbs(path)],
    };
  }

  const graph = {
    "/": [organization(), website(), softwareApplication({ priced: false }), faqPage()],
    "/pricing": [webPage(path), softwareApplication({ withOffers: true }), breadcrumbs(path)],
    "/about": [webPage(path), organization({ full: true }), breadcrumbs(path)],
    "/contact": [webPage(path), organization({ full: true }), breadcrumbs(path)],
  }[path] ?? [webPage(path), breadcrumbs(path)];

  return { "@context": "https://schema.org", "@graph": graph };
}
